import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, ModalHeader } from 'reactstrap';
import { APIClient } from '../../utils/APIClient';
import { uploadWithProgress } from '../../utils/multipart';
import { API_URL } from '../../settings/local_settings';
import { isSuperAdmin } from '../../utils/FeatureFlags';
import { useReportModality } from '../../hooks/useReportModality';
import { Spinner } from '../Spinner';
import { FaIcon } from '../FaIcon';
import { toast } from 'react-toastify';
import { InviteUserForm } from '../invitations/InviteUserModal';
import { PortalHeroStrip } from './PortalHeroStrip';
import { PortalModulesGrid } from './portal_modules/PortalModulesGrid';
import { portalInviteFeatures } from './portal_modules/portalInviteFeatures';
import { PortalAppearanceZone } from './PortalAppearanceZone';
import { BRAND_SOURCE_AGENCY, brandingForSource, deriveBrandSource } from './portalBrandSource';
import { resolvePreviewBranding } from './preview/previewBranding';
import { PortalUsersTable } from './PortalUsersTable';
import { PortalUsageSection } from './portal_usage/PortalUsageSection';

const DEFAULT_MODULES = { dashboard: false, reports: true, connect: true, connections: false, memory: false };

export const ClientPortalTab = ({ clientId, clientName }) => {
    const { t } = useTranslation();
    const [config, setConfig] = useState(null);
    const [users, setUsers] = useState(null);
    const [pendingInvitations, setPendingInvitations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [branding, setBranding] = useState({ logo_url: '', display_name_for_clients: '', brand_primary_color: '', brand_secondary_color: '' });
    const [savingBranding, setSavingBranding] = useState(false);
    const [uploadingLogo, setUploadingLogo] = useState(false);
    // De dónde sale la marca del portal (agencia / ícono del cliente / propia), deducido de lo
    // guardado; el ícono del cliente (`icon_url`, aparte de la marca); y la marca de la agencia, que
    // es la que se hereda (`undefined` = cargando, `null` = no se pudo leer).
    const [brandSource, setBrandSource] = useState(BRAND_SOURCE_AGENCY);
    const [iconUrl, setIconUrl] = useState(null);
    const [agencyBranding, setAgencyBranding] = useState(undefined);
    const [portalTheme, setPortalTheme] = useState('IMMERSIVE');
    const [savingTheme, setSavingTheme] = useState(false);
    // Token que remonta el iframe de preview cuando se guarda marca o cambia el tema.
    const [previewToken, setPreviewToken] = useState(0);
    // Por el hook y no por la lectura sincrónica del LocalStorage: con el seed frío (primer
    // login, otro navegador) la lectura devuelve la modalidad ausente y esta pantalla se
    // quedaba mostrando el módulo de la modalidad vieja hasta un F5.
    const { modality: reportModality } = useReportModality();
    const superAdmin = isSuperAdmin();

    const fetchUsers = useCallback(() => {
        new APIClient(API_URL + `client/${clientId}/portal/users/`).call('GET').then((data) => {
            if (data && data.status === 'OK') {
                setUsers(data.data.users || []);
                setPendingInvitations(data.data.pending_invitations || []);
            }
        });
    }, [clientId]);

    useEffect(() => {
        Promise.all([
            new APIClient(API_URL + `client/${clientId}/portal/config/`).call('GET'),
            new APIClient(API_URL + `client/${clientId}/portal/users/`).call('GET'),
        ]).then(([configRes, usersRes]) => {
            if (configRes?.status === 'OK') {
                setConfig(configRes.data);
                setPortalTheme(configRes.data.portal_theme || 'IMMERSIVE');
                if (configRes.data.branding) {
                    setBranding(formBranding(configRes.data.branding));
                }
                setIconUrl(configRes.data.icon_url || null);
                setBrandSource(deriveBrandSource(configRes.data.branding, configRes.data.icon_url));
            }
            if (usersRes?.status === 'OK') {
                setUsers(usersRes.data.users || []);
                setPendingInvitations(usersRes.data.pending_invitations || []);
            }
            setLoading(false);
        });
    }, [clientId]);

    // La marca de la agencia, para mostrarla en «La marca de tu agencia» (o avisar que no tiene). No
    // gatea la pantalla: si falla, esa opción sólo no la muestra. `cache` comparte el pedido con el resto
    // de la app EN ESTA VENTANA (el iframe de la vista previa es otro documento, con su propio caché), y
    // Perfil de la agencia lo limpia al subir, quitar o guardar la marca: si no, durante 30 s esta
    // pantalla seguía diciendo «Tu agencia todavía no tiene marca» (review C-iv-icono-y-marca-03).
    useEffect(() => {
        new APIClient(API_URL + 'get-agency-data/').call('GET', {}, { cache: true }).then((data) => {
            setAgencyBranding(data?.status === 'OK' && data.general_data ? data.general_data : null);
        }).catch(() => setAgencyBranding(null));
    }, []);

    const handleBrandingChange = (e) => {
        const { name, value } = e.target;
        setBranding(prev => ({ ...prev, [name]: value }));
    };

    // Guarda lo que muestra la opción elegida: con «la marca de tu agencia», el override vacío entero
    // (logo, nombre y colores: el cliente hereda); con «el ícono», el ícono como logo.
    const handleSaveBranding = () => {
        setSavingBranding(true);
        new APIClient(API_URL + `client/${clientId}/portal/config/`).call('PUT', {
            branding: brandingForSource(brandSource, branding, iconUrl),
        }).then((data) => {
            setSavingBranding(false);
            if (data?.status === 'OK') {
                toast.success(t('portal_tab.branding_saved'));
                setConfig(prev => ({ ...prev, branding: data.data.branding }));
                // El formulario NO se vacía con lo guardado (fede, 10/10, review C-iv-icono-y-marca-06): si
                // se pasó a «La marca de tu agencia» por error, lo cargado sigue en pantalla hasta recargar
                // y alcanza con volver a «Una marca propia» y guardar (el archivo sigue en el bucket).
                setBrandSource(deriveBrandSource(data.data.branding, iconUrl));
                setPreviewToken(p => p + 1);
            } else {
                toast.error(t('portal_tab.branding_save_error'));
            }
        }).catch(() => {
            setSavingBranding(false);
            toast.error(t('portal_tab.branding_save_error'));
        });
    };

    // Los mensajes del backend están escritos en español, y acá el rechazo (tipo o tamaño) es el
    // camino NORMAL, no un borde: se traduce por `reason_code` y el texto del servidor queda como
    // último recurso para lo que todavía no tenga clave.
    const logoErrorMessage = (data) => {
        let reason = null;
        try {
            reason = JSON.parse(data?.errors || '{}')?.reason_code;
        } catch (e) {
            reason = null;
        }
        const key = reason && `portal_tab.branding_logo_error_${reason}`;
        const translated = key && t(key, { defaultValue: '' });
        return translated || data?.error_message || t('portal_tab.branding_logo_upload_error');
    };

    // Carga directa del logo: se sube el archivo y el backend PERSISTE la URL resultante en el
    // branding del cliente, sin esperar al botón «Guardar». El usuario elige la imagen y el logo ya
    // quedó puesto — que es la fricción que la URL a mano nunca resolvió.
    const handleUploadLogo = (file) => {
        setUploadingLogo(true);
        const formData = new FormData();
        formData.append('file', file);
        uploadWithProgress(API_URL + `client/${clientId}/portal/logo/`, formData).then((data) => {
            setUploadingLogo(false);
            if (data?.status === 'OK' && data.data?.logo_url) {
                setBranding(prev => ({ ...prev, logo_url: data.data.logo_url }));
                setConfig(prev => ({
                    ...prev,
                    branding: { ...(prev?.branding || {}), logo_url: data.data.logo_url },
                }));
                setPreviewToken(p => p + 1);
                toast.success(t('portal_tab.branding_logo_uploaded'));
            } else {
                toast.error(logoErrorMessage(data));
            }
        // Sin este catch, un fallo interno del helper dejaba el spinner de «subiendo» prendido
        // para siempre y el campo inutilizable hasta recargar.
        }).catch(() => {
            setUploadingLogo(false);
            toast.error(t('portal_tab.branding_logo_upload_error'));
        });
    };

    // El logo lo rechazó el navegador, sin viajar al servidor (tipo o tamaño).
    const handleRejectLogo = (messageKey) => toast.error(t(messageKey));

    // Quitar es simétrico a subir: persiste en el acto, sin pasar por «Guardar». El cliente vuelve
    // a heredar el logo de la agencia.
    const handleRemoveLogo = () => {
        // Quitar persiste en el acto y el archivo se borra del bucket: no hay deshacer. Un clic
        // accidental dejaba al cliente heredando el logo de la agencia sin vuelta atrás, así que
        // pregunta antes — mismo patrón que el borrado de informes (`ClientReportsContent`).
        if (!window.confirm(t('portal_tab.branding_logo_remove_confirm'))) return;
        setUploadingLogo(true);
        new APIClient(API_URL + `client/${clientId}/portal/logo/`).call('DELETE').then((data) => {
            setUploadingLogo(false);
            if (data?.status === 'OK') {
                setBranding(prev => ({ ...prev, logo_url: '' }));
                setConfig(prev => ({ ...prev, branding: { ...(prev?.branding || {}), logo_url: '' } }));
                setPreviewToken(p => p + 1);
                // Subir avisa que salió bien; quitar tiene que avisar igual.
                toast.success(t('portal_tab.branding_logo_removed'));
            } else {
                toast.error(logoErrorMessage(data));
            }
        }).catch(() => setUploadingLogo(false));
    };

    const handleSelectTheme = (themeId) => {
        if (themeId === portalTheme || savingTheme) return;
        const previous = portalTheme;
        setPortalTheme(themeId);
        setSavingTheme(true);
        new APIClient(API_URL + `client/${clientId}/portal/config/`).call('PUT', {
            portal_theme: themeId,
        }).then((data) => {
            setSavingTheme(false);
            if (data?.status === 'OK') {
                toast.success(t('portal_tab.theme_saved'));
                setPreviewToken(p => p + 1);
            } else {
                setPortalTheme(previous);
                toast.error(t('portal_tab.theme_save_error'));
            }
        }).catch(() => {
            setSavingTheme(false);
            setPortalTheme(previous);
            toast.error(t('portal_tab.theme_save_error'));
        });
    };

    const handleToggleModule = (flag, value) => {
        const prevModules = config?.modules || DEFAULT_MODULES;
        setConfig(prev => ({ ...prev, modules: { ...prevModules, [flag]: value } }));
        new APIClient(API_URL + `client/${clientId}/portal/config/`).call('PUT', {
            modules: { [flag]: value },
        }).then((data) => {
            if (data?.status === 'OK' && data.data?.modules) {
                setConfig(prev => ({ ...prev, modules: data.data.modules }));
                setPreviewToken(p => p + 1); // refresca el preview en vivo para reflejar el módulo
                // El backend contesta OK aunque haya DESCARTADO el flag (una capability que el
                // rol no puede tocar se descarta en silencio). Sin este chequeo el toggle rebota
                // a su valor previo sin decir nada: ese síntoma fantasma hizo que el gate del
                // Tablero se reportara como bug tres veces (cards WMYDE3Eg / xKynpjFn).
                // Vale también en la ventana de deploy BE-viejo/FE-nuevo.
                if (data.data.modules[flag] !== value) {
                    toast.error(t('portal_modules.module_not_applied'));
                }
            } else {
                setConfig(prev => ({ ...prev, modules: prevModules }));
                toast.error(t('portal_modules.module_save_error'));
            }
        }).catch(() => {
            setConfig(prev => ({ ...prev, modules: prevModules }));
            toast.error(t('portal_modules.module_save_error'));
        });
    };

    // Asignar la primera vista prende Reportes en el BACKEND (P6): re-leemos la config para
    // que la card del módulo no siga diciendo "Desactivado" hasta un F5 (QA H3, 2026-08-27).
    const refreshModulesFromServer = useCallback(() => {
        new APIClient(API_URL + `client/${clientId}/portal/config/`).call('GET').then((data) => {
            if (data?.status === 'OK' && data.data?.modules) {
                setConfig(prev => ({ ...prev, modules: data.data.modules }));
                setPreviewToken(p => p + 1);
            }
        });
    }, [clientId]);

    if (loading) {
        return (
            <div className="section-body h-items">
                <div className="section-body-item -wider box" style={{ padding: '3em', textAlign: 'center' }}>
                    <Spinner color="positive" />
                </div>
            </div>
        );
    }

    const modules = config?.modules || DEFAULT_MODULES;
    // El encabezado muestra lo que ve el cliente: la opción elegida sobre la marca de la agencia, con el
    // mismo resolvedor que la vista previa. Con el override crudo, «La marca de tu agencia» dibujaba el
    // cuadrado de Dashbo sin logo (review C-iv-icono-y-marca-05).
    const heroBrand = resolvePreviewBranding(brandingForSource(brandSource, branding, iconUrl),
        agencyBranding || null);
    const activeModulesCount = Object.values(modules).filter(Boolean).length;
    const usersCount = users ? users.length : 0;

    // Features por defecto para sembrar la invitación (incluye las claves FE-only que el portal no maneja).
    const defaultClientFeatures = portalInviteFeatures(modules);

    return (
        <div className="section-body h-items">
            <div className="section-body-item -wider portal-tab pm-tab">
                <PortalHeroStrip
                    clientId={clientId}
                    clientName={clientName}
                    brandName={heroBrand.display_name_for_clients}
                    brandColor={heroBrand.brand_primary_color}
                    logoUrl={heroBrand.logo_url}
                    usersCount={usersCount}
                    activeModulesCount={activeModulesCount}
                    onInvite={() => setShowInviteModal(true)}
                />

                <PortalModulesGrid
                    clientId={clientId}
                    modules={modules}
                    onToggleModule={handleToggleModule}
                    isSuperAdmin={superAdmin}
                    reportModality={reportModality}
                    onAssignmentsChanged={refreshModulesFromServer}
                />

                <PortalAppearanceZone
                    clientId={clientId}
                    branding={branding}
                    onBrandingChange={handleBrandingChange}
                    onSaveBranding={handleSaveBranding}
                    savingBranding={savingBranding}
                    portalTheme={portalTheme}
                    onSelectTheme={handleSelectTheme}
                    savingTheme={savingTheme}
                    previewToken={previewToken}
                    onUploadLogo={handleUploadLogo}
                    onRemoveLogo={handleRemoveLogo}
                    onRejectLogo={handleRejectLogo}
                    uploadingLogo={uploadingLogo}
                    brandSource={brandSource}
                    onBrandSourceChange={setBrandSource}
                    iconUrl={iconUrl}
                    agencyBranding={agencyBranding}
                />

                <PortalUsersTable
                    clientId={clientId}
                    users={users}
                    pendingInvitations={pendingInvitations}
                    onInvite={() => setShowInviteModal(true)}
                    onRefresh={fetchUsers}
                />

                {/* «A quién le di acceso» → «qué hizo con él». Va justo debajo de los usuarios y
                    trae sus datos por su cuenta: no entra al Promise.all de arriba, que gatea el
                    render entero de la pantalla con un spinner de pantalla completa. */}
                <PortalUsageSection
                    clientId={clientId}
                    onInvite={() => setShowInviteModal(true)}
                />
            </div>

            {/* Con el cupo lleno el límite va encima del formulario y el modal pasa de 900 px: el cuerpo
                scrollea y «Enviar» queda a la vista (QA-VIS-cupo-06, `.invite-portal-modal`). */}
            <Modal isOpen={showInviteModal} toggle={() => setShowInviteModal(false)}
                   scrollable className="invite-portal-modal">
                <ModalHeader toggle={() => setShowInviteModal(false)}>
                    <FaIcon iconName="user-plus" />
                    {t('portal_tab.invite_title')}
                </ModalHeader>
                <InviteUserForm
                    clientOnly
                    fixedClientId={clientId}
                    defaultClientFeatures={defaultClientFeatures}
                    defaultPortalLanguage={config?.language}
                    afterSubmit={(sent) => {
                        setShowInviteModal(false);
                        fetchUsers();
                        // El idioma elegido quedó como el del portal de este cliente: la próxima
                        // invitación lo trae elegido sin recargar la pantalla. Con un usuario que ya
                        // existía (`attached`) el backend no lo guardó.
                        if (sent && sent.portalLanguage && !sent.attached) {
                            setConfig(prev => ({ ...prev, language: sent.portalLanguage }));
                        }
                    }}
                />
            </Modal>
        </div>
    );
};

// El override del backend (con `null`) como lo usa el formulario (con '').
function formBranding(branding) {
    const value = branding || {};
    return {
        logo_url: value.logo_url || '',
        display_name_for_clients: value.display_name_for_clients || '',
        brand_primary_color: value.brand_primary_color || '',
        brand_secondary_color: value.brand_secondary_color || '',
    };
}
